<template>
  <div class="jar-panel card border-0 shadow-sm rounded-lg overflow-hidden h-100">
    <!-- Panel Header / Toggles -->
    <div class="panel-header d-flex justify-content-between align-items-center bg-white px-3 py-2 border-bottom">
      <!-- Visualization Mode Toggle -->
      <div v-if="!hideToggles" class="btn-group toggle-group" role="group">
        <button
          type="button"
          class="btn btn-sm toggle-btn"
          :class="{ active: localViewType === 'bar' }"
          @click="setViewType('bar')"
        >
          <b-icon icon="bar-chart-fill" class="mr-1" /> {{ (isTrend || forceColumn) ? 'Chart' : 'Bar' }}
        </button>
        <button
          type="button"
          class="btn btn-sm toggle-btn"
          :class="{ active: localViewType === 'map' }"
          :disabled="disableMap"
          @click="setViewType('map')"
        >
          <b-icon icon="map-fill" class="mr-1" /> Map
        </button>
      </div>

      <!-- Utility Action Buttons -->
      <div class="d-flex align-items-center utility-actions">
        <button
          @click="toggleFullscreen"
          class="btn btn-outline-secondary btn-square mx-1 p-0 d-flex align-items-center justify-content-center"
          title="Toggle Fullscreen"
        >
          <b-icon :icon="isFullscreen ? 'arrows-angle-contract' : 'arrows-angle-expand'" />
        </button>
        <button
          @click="downloadChartImage"
          class="btn btn-outline-secondary btn-square mx-1 p-0 d-flex align-items-center justify-content-center"
          title="Download Chart"
        >
          <b-icon icon="download" />
        </button>
      </div>
    </div>

    <!-- Panel Content Area -->
    <div class="panel-body bg-white p-3 d-flex flex-column justify-content-between flex-grow-1">
      <!-- Rural / Urban Badge Row (Placeholder) -->
      <div v-if="ruralValue && urbanValue && localViewType === 'bar'" class="d-flex align-items-center mb-3 px-3 py-2 rounded" style="gap: 1.5rem; background-color: #f8fafc; border: 1px solid #e2e8f0; width: fit-content; margin-left: 1rem;">
        <div class="d-flex align-items-center" style="font-size: 0.9rem; color: #475569;">
          <span class="mr-2" style="font-size: 1.1rem;">🏡</span>
          <strong>Rural:</strong> <span class="ml-1 font-weight-bold text-success" style="color: #006C47 !important;">{{ ruralValue }}%</span>
        </div>
        <div class="d-flex align-items-center" style="font-size: 0.9rem; color: #475569;">
          <span class="mr-2" style="font-size: 1.1rem;">🏢</span>
          <strong>Urban:</strong> <span class="ml-1 font-weight-bold text-success" style="color: #006C47 !important;">{{ urbanValue }}%</span>
        </div>
      </div>
      <!-- Gender Badge Row (Placeholder) -->
      <div v-if="femaleValue && maleValue && localViewType === 'bar'" class="d-flex align-items-center mb-3 px-3 py-2 rounded" style="gap: 1.5rem; background-color: #fff1f2; border: 1px solid #fecdd3; width: fit-content; margin-left: 1rem;">
        <div class="d-flex align-items-center" style="font-size: 0.9rem; color: #9f1239;">
          <span class="mr-2" style="font-size: 1.1rem;">🚺</span>
          <strong>Female:</strong> <span class="ml-1 font-weight-bold text-danger">{{ femaleValue }}%</span>
        </div>
        <div class="d-flex align-items-center" style="font-size: 0.9rem; color: #1e3a8a;">
          <span class="mr-2" style="font-size: 1.1rem;">🚹</span>
          <strong>Male:</strong> <span class="ml-1 font-weight-bold text-primary">{{ maleValue }}%</span>
        </div>
      </div>
      <!-- Highcharts Container -->
      <div class="chart-container flex-grow-1 position-relative" style="min-height: 480px;">
        <highcharts
          v-if="localViewType === 'bar'"
          :options="barOptions"
          ref="barChart"
          style="width: 100%; height: 100%;"
        />
        <Highmaps
          v-else-if="localViewType === 'map'"
          :options="mapOptions"
          ref="mapChart"
          style="width: 100%; height: 100%;"
        />
        <!-- Right side source label styled according to the design -->
        <div v-if="localViewType === 'map'" class="map-source-label position-absolute font-weight-bold text-dark work-sans" style="bottom: 25px; right: 25px; font-size: 0.8rem; z-index: 10; opacity: 0.85; letter-spacing: 0.5px;">
          {{ source }}
        </div>
      </div>

      <!-- Commentary Box (for Panel 1 / Primary Source under Map View) -->
      <div
        v-if="commentary && showCommentary"
        class="commentary-box mt-3 p-3 rounded"
      >
        <p class="text-secondary small mb-0 work-sans pre-line-wrap" style="color: #334155 !important; font-size: 0.95rem; line-height: 1.5;">
          {{ commentary }}
        </p>
      </div>

      <!-- Custom HTML Legend Box (for Panel 2 / Secondary Source under Map View) -->
      <div
        v-if="localViewType === 'map' && showLegend && legend && legend.length"
        class="legend-box mt-3 p-2 bg-light border rounded"
      >
        <div class="d-flex flex-wrap align-items-center justify-content-around">
          <div
            v-for="(item, index) in legend"
            :key="index"
            class="d-flex align-items-center mx-2 my-1"
          >
            <span
              class="legend-color-indicator rounded-sm mr-2"
              :style="{ backgroundColor: item.color, width: '18px', height: '18px', display: 'inline-block' }"
            />
            <span class="small font-weight-bold text-muted work-sans">
              {{ item.label }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Expand Modal using Bootstrap Vue (consistent with MSDAT dashboards) -->
    <b-modal
      :id="modalId"
      size="xl"
      hide-footer
      centered
      :title="modalTitle"
      @shown="onModalShown"
      @hidden="onModalHidden"
    >
      <div class="p-3">
        <div style="height: 600px; position: relative;">
          <highcharts
            v-if="localViewType === 'bar' && modalActive"
            :options="barOptionsModal"
            ref="barChartModal"
            style="width: 100%; height: 100%;"
          />
          <Highmaps
            v-else-if="localViewType === 'map' && modalActive"
            :options="mapOptionsModal"
            ref="mapChartModal"
            style="width: 100%; height: 100%;"
          />
          <!-- Label matching the source -->
          <div v-if="localViewType === 'map'" class="map-source-label position-absolute font-weight-bold text-dark work-sans" style="bottom: 25px; right: 25px; font-size: 0.9rem; z-index: 10; opacity: 0.85;">
            {{ source }}
          </div>
        </div>

        <!-- Commentary Box (under map) -->
        <div
          v-if="commentary && showCommentary"
          class="commentary-box mt-3 p-3 rounded"
        >
          <p class="text-secondary small mb-0 work-sans pre-line-wrap" style="color: #334155 !important; font-size: 0.95rem; line-height: 1.5;">
            {{ commentary }}
          </p>
        </div>

        <!-- Legend Box (under map) -->
        <div
          v-if="localViewType === 'map' && showLegend && legend && legend.length"
          class="legend-box mt-3 p-2 border rounded"
        >
          <div class="d-flex flex-wrap align-items-center justify-content-around">
            <div
              v-for="(item, index) in legend"
              :key="index"
              class="d-flex align-items-center mx-2 my-1"
            >
              <span
                class="legend-color-indicator rounded-sm mr-2"
                :style="{ backgroundColor: item.color, width: '18px', height: '18px', display: 'inline-block' }"
              />
              <span class="small font-weight-bold text-muted work-sans">
                {{ item.label }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Highcharts from 'highcharts';
import loadMap from 'highcharts/modules/map';
import { genComponent } from 'vue-highcharts';
import NigerianMap from '@/components/maps/ng-all.geo.json';

// Initialize map module
loadMap(Highcharts);

export default {
  name: 'JarPanel',
  components: {
    Highmaps: genComponent('Highmaps', Highcharts),
  },
  props: {
    source: {
      type: String,
      required: true,
    },
    data: {
      type: [Array, Object],
      required: true,
    },
    viewType: {
      type: String,
      default: 'bar',
    },
    legend: {
      type: Array,
      default: () => [],
    },
    commentary: {
      type: String,
      default: '',
    },
    showCommentary: {
      type: Boolean,
      default: false,
    },
    showLegend: {
      type: Boolean,
      default: false,
    },
    indicatorName: {
      type: String,
      default: '',
    },
    unit: {
      type: String,
      default: '',
    },
    chartTitle: {
      type: String,
      default: '',
    },
    ruralValue: {
      type: [String, Number],
      default: null,
    },
    urbanValue: {
      type: [String, Number],
      default: null,
    },
    femaleValue: {
      type: [String, Number],
      default: null,
    },
    maleValue: {
      type: [String, Number],
      default: null,
    },
    forceColumn: {
      type: Boolean,
      default: false,
    },
    disableMap: {
      type: Boolean,
      default: false,
    },
    noSort: {
      type: Boolean,
      default: false,
    },
    panelId: {
      type: String,
      default: '',
    },
    customOptions: {
      type: Object,
      default: null,
    },
    hideToggles: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localViewType: this.viewType,
      modalActive: false,
    };
  },
  watch: {
    viewType(newVal) {
      this.localViewType = newVal;
    },
  },
  computed: {
    modalId() {
      const uniquePart = (this.indicatorName || this.chartTitle || '').replace(/[^a-zA-Z0-9]/g, '');
      const cleanSource = this.source.replace(/[^a-zA-Z0-9]/g, '');
      const suffix = this.panelId ? `-${this.panelId}` : '';
      return `modal-expand-${uniquePart}-${cleanSource}${suffix}`;
    },
    modalTitle() {
      return `${this.indicatorName} (${this.source})`;
    },
    isTrend() {
      const activeData = this.chartDataList;
      return activeData.length > 0 && /^\d{4}$/.test(activeData[0].state || activeData[0][0] || '');
    },
    chartDataList() {
      if (!this.data) return [];
      if (Array.isArray(this.data)) {
        return this.data;
      }
      if (this.localViewType === 'bar') {
        return this.data.trend || this.data.states || [];
      }
      return this.data.states || this.data.trend || [];
    },
    sortedData() {
      const list = this.chartDataList;
      const formatVal = (v) => {
        if (typeof v !== 'number') return v;
        return Math.round(v * 10) / 10;
      };

      if (this.noSort) {
        return list.map((item) => [item.state, formatVal(item.value)]);
      }
      if (this.isTrend) {
        return [...list]
          .sort((a, b) => parseInt(a.state, 10) - parseInt(b.state, 10))
          .map((item) => [item.state, formatVal(item.value)]);
      }
      return [...list]
        .sort((a, b) => b.value - a.value)
        .map((item) => [item.state, formatVal(item.value)]);
    },
    barOptions() {
      if (this.customOptions) {
        return {
          ...this.customOptions,
          credits: { enabled: false },
        };
      }
      const vm = this;
      const isTrendChart = vm.isTrend || vm.forceColumn;
      const sorted = vm.sortedData;

      // Color mapping for columns if it is trend data
      const trendColors = ['#9CD39E', '#BCE4C0', '#006C47'];
      const formattedSeriesData = sorted.map((point, idx) => {
        if (vm.isTrend) {
          return {
            name: point[0],
            y: point[1],
            color: trendColors[idx % trendColors.length],
          };
        }
        return {
          name: point[0],
          y: point[1],
          color: '#006C47',
        };
      });

      return {
        chart: {
          type: isTrendChart ? 'column' : 'bar',
          backgroundColor: 'transparent',
          style: {
            fontFamily: '"Inter", sans-serif',
          },
        },
        title: {
          text: vm.chartTitle || null,
          align: 'center',
          verticalAlign: 'bottom',
          style: {
            fontFamily: '"Inter", sans-serif',
            fontSize: '13px',
            fontWeight: 'bold',
            color: '#475569',
          },
        },
        credits: { enabled: false },
        xAxis: {
          type: 'category',
          labels: {
            style: {
              fontSize: '11px',
              fontWeight: 'bold',
              color: '#34495e',
            },
          },
          gridLineWidth: 0,
          lineWidth: 0,
          tickWidth: 0,
        },
        yAxis: {
          title: {
            text: vm.unit ? `${vm.indicatorName} (${vm.unit})` : vm.indicatorName,
            style: {
              fontWeight: 'bold',
              color: '#7f8c8d',
            },
          },
          gridLineColor: '#f1f2f6',
          lineWidth: isTrendChart ? 1 : 0,
        },
        tooltip: {
          headerFormat: '<span style="font-size: 10px">{point.key}</span><br/>',
          pointFormat: `<span style="color:{point.color}">●</span> ${vm.source}: <b>{point.y}</b> ${vm.unit}<br/>`,
        },
        legend: { enabled: false },
        plotOptions: {
          bar: {
            color: '#006C47',
            borderRadius: 3,
            dataLabels: {
              enabled: true,
              inside: false,
              crop: false,
              overflow: 'allow',
              style: {
                fontSize: '11px',
                fontWeight: 'bold',
                color: '#2c3e50',
                textOutline: 'none',
              },
            },
          },
          column: {
            borderRadius: 4,
            dataLabels: {
              enabled: true,
              inside: true,
              style: {
                fontWeight: 'bold',
                fontSize: '13px',
                color: '#ffffff',
                textOutline: 'none',
              },
            },
            pointWidth: 90,
          },
        },
        series: [{
          name: vm.source,
          data: formattedSeriesData,
        }],
      };
    },
    mapOptions() {
      const vm = this;
      const list = this.chartDataList;

      // Parse map data into Highcharts objects format
      const formattedMapData = list.map((item) => ({
        name: item.state,
        value: typeof item.value === 'number' ? Math.round(item.value) : item.value,
      }));

      return {
        chart: {
          map: NigerianMap,
          backgroundColor: 'transparent',
          style: {
            fontFamily: '"Inter", sans-serif',
          },
        },
        title: { text: null },
        credits: { enabled: false },
        mapNavigation: {
          enabled: true,
          buttonOptions: {
            verticalAlign: 'bottom',
            theme: {
              fill: 'white',
              'stroke-width': 1,
              stroke: '#e2e8f0',
              r: 4,
              states: {
                hover: {
                  fill: '#f8fafc',
                },
              },
            },
          },
        },
        colorAxis: {
          dataClasses: this.legend.map((item) => ({
            from: item.from,
            to: item.to,
            color: item.color,
            name: item.label,
          })),
        },
        tooltip: {
          headerFormat: '',
          pointFormat: `<b>{point.name}</b><br/>${vm.source}: <b>{point.value}</b> ${vm.unit}`,
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          map: {
            joinBy: ['name', 'name'],
            borderColor: '#ffffff',
            borderWidth: 1.2,
            states: {
              hover: {
                color: '#cbd5e1',
                borderColor: '#475569',
              },
            },
            dataLabels: {
              enabled: true,
              format: '{point.name}<br/>{point.value}',
              style: {
                fontSize: '9px',
                fontWeight: 'bold',
                color: '#1e293b',
                textOutline: 'none',
                textAlign: 'center',
              },
            },
          },
        },
        series: [{
          name: this.source,
          data: formattedMapData,
        }],
      };
    },
    barOptionsModal() {
      const options = { ...this.barOptions };
      options.chart = { ...options.chart, height: 600 };
      return options;
    },
    mapOptionsModal() {
      const options = { ...this.mapOptions };
      options.chart = { ...options.chart, height: 600 };
      return options;
    },
  },
  methods: {
    setViewType(type) {
      this.localViewType = type;
      this.$emit('view-change', type);
    },
    toggleFullscreen() {
      this.$bvModal.show(this.modalId);
    },
    downloadChartImage() {
      const chartRef = this.localViewType === 'bar' ? this.$refs.barChart : this.$refs.mapChart;
      if (chartRef && chartRef.chart) {
        chartRef.chart.exportChart();
      }
    },
    onModalShown() {
      this.modalActive = true;
      this.$nextTick(() => {
        const chartRef = this.localViewType === 'bar' ? this.$refs.barChartModal : this.$refs.mapChartModal;
        if (chartRef && chartRef.chart) {
          chartRef.chart.reflow();
        }
      });
    },
    onModalHidden() {
      this.modalActive = false;
    },
  },
};
</script>

<style scoped>
.jar-panel {
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.panel-header {
  border-bottom: 1px solid #f1f2f6;
  background-color: #ffffff;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.toggle-group {
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  overflow: hidden;
  padding: 2px;
  background-color: #f8fafc;
}

.toggle-btn {
  border: none !important;
  border-radius: 4px !important;
  color: #64748b;
  font-weight: 600;
  font-size: 0.75rem;
  padding: 0.3rem 0.75rem;
  background-color: transparent;
  transition: all 0.2s ease;
}

.toggle-btn.active {
  background-color: #006C47 !important;
  color: #ffffff !important;
}

.utility-actions .hover-green:hover {
  color: #006C47 !important;
}

.btn-square {
  width: 32px;
  height: 32px;
  border-color: #cbd5e1;
  border-radius: 6px;
  color: #64748b;
  transition: all 0.2s ease;
}

.btn-square:hover {
  background-color: #f1f5f9;
  border-color: #006C47;
  color: #006C47;
}

.letter-spacing-1 {
  letter-spacing: 0.5px;
}

.commentary-box {
  background-color: #EBF4F6;
  border: 1px solid #CDE5DF;
  color: #1e293b;
  border-radius: 6px;
}

.legend-box {
  background-color: #EBF4F6;
  border: 1px solid #CDE5DF;
  border-radius: 6px;
}

.pre-line-wrap {
  white-space: pre-line;
}

.legend-color-indicator {
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: inset 0 0 2px rgba(0, 0, 0, 0.15);
}

.jar-panel * {
  font-family: 'Inter', sans-serif !important;
}
</style>
